import { CanConnectOrBindSuiteErrors } from '../../typing';
import { isCountryCodeSubdomain, parseDomain } from '../../utils';
import { ConnectionType } from '@wix/ambassador-premium-domains-v1-connected-domain/types';
import { ManagementTypeEnumManagementType } from '@wix/ambassador-domains-userdomain-v1-user-domain/types';
export const canConnectOrBindDomainSuite = async _ref => {
  let httpClient = _ref.httpClient,
    _ref$request = _ref.request,
    domain = _ref$request.domain,
    msid = _ref$request.msid;
  if (!domain) {
    return {
      canConnectOrBind: false
    };
  }
  const parsedDomain = domain.asciiDomain ? domain : parseDomain(domain);
  const rootDomain = parsedDomain.asciiDomain;
  const fullAsciiDomain = parsedDomain.fullAsciiDomain;
  const isSubdomain = !!parsedDomain.subdomain;
  if (isSubdomain) {
    return canConnectOrBindSubdomain({
      httpClient,
      subdomain: fullAsciiDomain,
      rootDomain,
      msid
    });
  } else {
    return canConnectOrBindRootDomain({
      httpClient,
      rootDomain,
      msid
    });
  }
};
async function canConnectOrBindRootDomain(_ref2) {
  let httpClient = _ref2.httpClient,
    rootDomain = _ref2.rootDomain,
    msid = _ref2.msid;
  const _await$import$Domains = (await import('../ApiClients/DomainsApiClient')).DomainsApiClient.queries({
      httpClient
    }),
    checkDomainIsTaken = _await$import$Domains.checkDomainIsTaken,
    userDomain = _await$import$Domains.userDomain;
  const _await$userDomain = await userDomain({
      domain: rootDomain,
      withSiteData: true
    }, {
      cacheTime: 0
    }),
    userDomainError = _await$userDomain.error,
    site = _await$userDomain.site;
  const isRootDomainInAccount = !userDomainError;
  if (userDomainError === 'belongsToAnotherAccount') {
    return {
      error: CanConnectOrBindSuiteErrors.DOMAIN_TAKEN_IN_WIX,
      canConnectOrBind: false
    };
  }
  if (userDomainError === 'unauthorized') {
    return {
      canConnectOrBind: false,
      error: CanConnectOrBindSuiteErrors.UNAUTHORIZED
    };
  }
  if (isRootDomainInAccount) {
    if (site != null && site.siteId) {
      const metaSiteId = msid || site.siteId;
      return {
        error: metaSiteId === site.siteId ? CanConnectOrBindSuiteErrors.ALREADY_ASSIGNED_TO_THIS_SITE : CanConnectOrBindSuiteErrors.ALREADY_ASSIGNED,
        canConnectOrBind: true,
        assignedSite: site
      };
    }
    return {
      error: CanConnectOrBindSuiteErrors.FLOATING_DOMAIN,
      canConnectOrBind: true
    };
  }
  const _await$checkDomainIsT = await checkDomainIsTaken({
      domain: rootDomain
    }),
    isAvailable = _await$checkDomainIsT.isAvailable,
    error = _await$checkDomainIsT.error;
  if (!isAvailable && !error) {
    return {
      canConnectOrBind: true
    };
  }
  return {
    canConnectOrBind: false
  };
}
async function canConnectOrBindSubdomain(_ref3) {
  let httpClient = _ref3.httpClient,
    subdomain = _ref3.subdomain,
    rootDomain = _ref3.rootDomain;
  const _await$import$Domains2 = (await import('../ApiClients/DomainsApiClient')).DomainsApiClient.queries({
      httpClient
    }),
    getConnectedDomain = _await$import$Domains2.getConnectedDomain,
    userDomain = _await$import$Domains2.userDomain;
  const _await$Promise$all = await Promise.all([userDomain({
      domain: subdomain
    }, {
      cacheTime: 0
    }), userDomain({
      domain: rootDomain
    }, {
      cacheTime: 0
    })]),
    subdomainUserDomain = _await$Promise$all[0],
    rootDomainUserDomain = _await$Promise$all[1];
  const subdomainUserDomainError = subdomainUserDomain == null ? void 0 : subdomainUserDomain.error;
  const isSubdomainInAccount = !(subdomainUserDomain != null && subdomainUserDomain.error);
  const ccPremiumError = isCountryCodeSubdomain(subdomain) ? CanConnectOrBindSuiteErrors.LANGUAGE_CODE_SUBDOMAIN : undefined;
  if (isSubdomainInAccount) {
    return {
      canConnectOrBind: true,
      error: ccPremiumError || CanConnectOrBindSuiteErrors.SUBDOMAIN_ALREADY_ASSIGNED
    };
  }
  if (subdomainUserDomainError === 'belongsToAnotherAccount' || (rootDomainUserDomain == null ? void 0 : rootDomainUserDomain.error) === 'belongsToAnotherAccount') {
    return {
      error: CanConnectOrBindSuiteErrors.DOMAIN_TAKEN_IN_WIX,
      canConnectOrBind: false
    };
  }
  if (subdomainUserDomainError === 'unauthorized') {
    return {
      canConnectOrBind: false,
      error: CanConnectOrBindSuiteErrors.UNAUTHORIZED
    };
  }
  try {
    const _await$getConnectedDo = await getConnectedDomain({
        connectedDomainId: rootDomain
      }),
      connectedDomain = _await$getConnectedDo.connectedDomain;
    if ((connectedDomain == null ? void 0 : connectedDomain.connectionType) === ConnectionType.POINTING) {
      return {
        error: CanConnectOrBindSuiteErrors.ROOT_DOMAIN_IS_CONNECTED_BY_POINTING,
        canConnectOrBind: false
      };
    }
    const subdomainRootIsConnectedByDns = (connectedDomain == null ? void 0 : connectedDomain.connectionType) === ConnectionType.NAMESERVERS ? CanConnectOrBindSuiteErrors.SUBDOMAIN_ROOT_IS_CONNECTED_BY_DNS : undefined;
    return {
      canConnectOrBind: true,
      error: ccPremiumError || subdomainRootIsConnectedByDns
    };
  } catch (e) {
    var _e$response, _rootDomainUserDomain;
    const statusCode = (e == null || (_e$response = e.response) == null ? void 0 : _e$response.status) ?? (e == null ? void 0 : e.status);
    const subdomainRootIsRegistered = ((_rootDomainUserDomain = rootDomainUserDomain.domain) == null ? void 0 : _rootDomainUserDomain.managementType) === ManagementTypeEnumManagementType.REGISTRATION ? CanConnectOrBindSuiteErrors.SUBDOMAIN_ROOT_IS_REGISTERED : undefined;
    switch (statusCode) {
      case 404:
        {
          return {
            canConnectOrBind: true,
            error: ccPremiumError || subdomainRootIsRegistered
          };
        }
      default:
        return {
          canConnectOrBind: false
        };
    }
  }
}
//# sourceMappingURL=canConnectOrBindDomainSuite.js.map